import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiRouteConfig } from '../../../config/api.route.config';
import { catchError, map, tap } from 'rxjs/operators';
import { Action } from '../../common/model/action';
import { ActionLog } from './model/action-log';
import { ActionStatusType } from '../../common/enums/enums';
import {ManagerIssueAction} from "../model/issue";


@Injectable()
export class BuildingJobsService {

  changed$: Subject<any> = new Subject<any>();

  constructor(private httpClient: HttpClient, private config: ApiRouteConfig) {
  }

  public add(action: Action): Observable<Action> {
    return this.httpClient.post<Action>(
      this.config.JOBS_GET_ALL,
      action,
    );
  }

  public update(action: Action): Observable<Action> {
    return this.httpClient.put<Action>(
      this.config.JOBS_GET_ALL,
      action,
    );
  }

  public getActionsForIssue(issueId: number): Observable<Action[]> {
    return this.httpClient.get<Action[]>(this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_ACTIONS, [issueId.toString()]));
  }

  public addActionForIssue(issueId: number, action: Action): Observable<Action> {
    return this.httpClient.post<Action>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_ACTIONS, [`${issueId}`]),
      action
    );
  }

  public assignJob(jobId: string, interventionTeam: string): Observable<void> {
    const route = this.config.getRouteWithInjectedParams(this.config.JOBS_ASSIGN, [jobId, interventionTeam]);

    return this.httpClient.put(route, null).pipe(map(_ => { return; }));
  }

  public unassignJob(jobId: string): Observable<void> {
    const route = this.config.getRouteWithInjectedParams(this.config.JOBS_UNASSIGN, [jobId]);

    return this.httpClient.put(route, null).pipe(map(_ => { return; }));
  }

  public assignJobWithPriority(jobId: string, interventionTeam: string, priority: number): Observable<void> {
    const route = this.config.getRouteWithInjectedParams(this.config.JOBS_ASSIGN_WITH_PRIORITY,
      [jobId, interventionTeam, priority.toString()]);

    return this.httpClient.put(route, null).pipe(map(_ => { return; }));
  }

  public completeJobs(jobIds: string[], jobStatus: ActionStatusType): Observable<void> {
    return this.httpClient.post(this.config.BUILDING_JOB_COMPLETE, {jobIds, status: jobStatus}).pipe(
      tap(() => this.changed$.next(null)),
      catchError(e => {
        return of(null);
      })
    );
  }

  public sendJob(jobId: string): Observable<void> {
    const route = this.config.getRouteWithInjectedParams(this.config.JOBS_SEND, [jobId]);
    return this.httpClient.post(route, null).pipe(map(_ => { return; }));
  }

  public getCreateActionUrl(issueGuids: string[]): Observable<string> {
    let params = new HttpParams();
    issueGuids.forEach(i => params = params.append("issueGuid", i));
    return this.httpClient.get<string>(this.config.BUILDING_JOB_CREATE_URL, {params});
  }

  public completeAction(action: Action): Observable<boolean> {
    return this.httpClient.post(this.config.BUILDING_JOB_COMPLETE, {jobIds: [action.id], status: action.status}).pipe(
      tap(() => this.changed$.next(null)),
      catchError(e => {
        return of(null);
      })
    );
  }

  public getActionLogs(actionId: string): Observable<ActionLog[]> {
    return this.httpClient
      .get<ActionLog[]>(this.config.getRouteWithInjectedParams(this.config.BUILDING_JOB_LOGS, [actionId]));
  }

  public getJobs(httpParams: HttpParams, pageNumber: number = 1, pageSize: number = 10): Observable<Action[]> {
    const params = httpParams
        .append('pageNumber', pageNumber.toString())
        .append('pageSize', pageSize.toString())

    return this.httpClient.get<Action[]>(this.config.JOBS_GET_ALL, {params});
  }

  updateJobStatus(jobId: string, status: ActionStatusType) {
      return this.httpClient.patch(this.config.getRouteWithInjectedParams(
        this.config.JOBS_UPDATE_STATUS,
        [jobId]
      ), {status}, { headers: {"Content-Type": "application/json"}}).pipe(
        tap(() => this.changed$.next(null)),
      );
  }
}
